import React, {
  CSSProperties,
  MouseEvent,
  ReactElement,
  SyntheticEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { Button, ButtonProps, Icon, SemanticICONS } from "semantic-ui-react";

type ButtonLoadCheckProps<T = undefined> = {
  // function
  setProp?: (key: any, value: any, callback?: () => any) => any;
  id?: string;
  className?: string;
  basic?: boolean;
  // config
  color?: ButtonProps["color"];
  disabled?: boolean;
  fluid?: boolean;
  icon?: SemanticICONS;
  iconRight?: SemanticICONS;
  iconStyle?: CSSProperties;
  name?: T;
  // data
  style?: CSSProperties;
  paramKey?: string;
  size?: ButtonProps["size"];
  title?: ReactElement | string;
  buttonLoadCheck?: string;
  onClick?: BLClickHandler<T>;
};

export type BLClickHandler<T = undefined> = (
  e: SyntheticEvent,
  data: ButtonProps & { name: T }
) => void;

const ButtonLoadCheck = (props: ButtonLoadCheckProps) => {
  const [iconName, setIconName] = useState<"cancel" | "check" | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [buttonColor, setButtonColor] = useState<ButtonProps["color"]>();

  const timeoutRef = useRef<any>(0);

  const handleReset = useCallback(
    (timeout = 2000) => {
      setIsLoading(false);

      timeoutRef.current = setTimeout(() => {
        setIconName(null);
        setButtonColor(undefined);

        props.setProp?.(`buttonLoadCheck.${props.paramKey}`, "");
      }, timeout);
    },
    [props.paramKey]
  );

  useEffect(() => {
    if (props.buttonLoadCheck === "LOADING") {
      setIsLoading(true);
      setIconName(null);

      clearTimeout(timeoutRef.current);

      return;
    }

    if (props.buttonLoadCheck === "SUCCESS") {
      setIconName("check");

      handleReset();
    } else if (props.buttonLoadCheck === "ERROR") {
      setIconName("cancel");
      setButtonColor("red");

      handleReset();
    } else if (!props.buttonLoadCheck && isLoading) {
      handleReset();
    }
  }, [handleReset, isLoading, props.buttonLoadCheck]);

  useEffect(() => {
    handleReset(0);

    return () => {
      handleReset(0);
    };
  }, []);

  // Handler
  const handleClick = (e: MouseEvent<HTMLButtonElement>, data: ButtonProps) => {
    if (props.buttonLoadCheck) {
      console.log("Button is in loading state or disabled");
    } else {
      props.onClick?.(e, { ...data, name: props.name });
    }
  };

  return (
    <Button
      id={props.id}
      className={props.className}
      style={
        "style" in props
          ? {
              ...props.style,
              ...(props.style?.border && {
                border: buttonColor === "red" ? "1px solid #db2828" : props.style.border,
              }),
              ...(props.style?.color && {
                color: buttonColor === "red" ? "#db2828" : props.style.color,
              }),
            }
          : undefined
      }
      basic={props.basic}
      color={buttonColor || props.color}
      disabled={props.disabled}
      fluid={props.fluid}
      icon={!!props.icon}
      loading={isLoading}
      name={props.name}
      size={props.size ?? "small"}
      onClick={handleClick}
    >
      <div style={{ position: "relative" }}>
        {!!iconName && (
          <Icon
            id="ButtonLoadCheck-Status"
            style={{
              ...props.iconStyle,
              alignItems: "center",
              display: "flex",
              height: "100%",
              justifyContent: "center",
              position: "absolute",
              width: "100%",
            }}
            name={iconName}
          />
        )}
        <span style={{ opacity: iconName || isLoading ? 0 : "" }}>
          {props.icon && (
            <Icon style={{ marginRight: props.title ? "0.428em" : 0 }} name={props.icon} />
          )}
          {props.title}
          {props.iconRight && <Icon style={{ marginLeft: "0.428em" }} name={props.iconRight} />}
        </span>
      </div>
    </Button>
  );
};

ButtonLoadCheck.displayName = "ButtonLoadCheck";

export default React.memo(ButtonLoadCheck) as <T extends string | undefined = undefined>(
  props: ButtonLoadCheckProps<T>
) => ReactElement;
